import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../../components/Shell";
import api from "../../lib/api";
import { Users, ClipboardList, Package, FileDown, Megaphone, Clock, Calendar } from "lucide-react";

export default function AdminDashboard() {
    const [summary, setSummary] = useState(null);
    const [recentClinics, setRecentClinics] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);

    useEffect(() => {
        api.get("/admin/summary").then(r => setSummary(r.data)).catch(() => {});
        api.get("/admin/clinics?status=pending").then(r => setRecentClinics((r.data || []).slice(0, 5))).catch(() => {});
        api.get("/admin/orders").then(r => setRecentOrders((r.data || []).slice(0, 5))).catch(() => {});
    }, []);

    return (
        <Shell variant="admin">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <p className="eyebrow mb-2">Admin</p>
                <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-10">Operations overview</h1>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                    <Tile to="/admin/clinics?status=pending" icon={<Clock size={18} strokeWidth={1.5} />} label="Pending approvals" value={summary?.clinics_pending ?? "—"} testid="tile-pending" highlight={(summary?.clinics_pending || 0) > 0} />
                    <Tile to="/admin/clinics" icon={<Users size={18} strokeWidth={1.5} />} label="Approved clinics" value={summary?.clinics_approved ?? "—"} testid="tile-approved" />
                    <Tile to="/admin/orders" icon={<ClipboardList size={18} strokeWidth={1.5} />} label="Open orders" value={summary?.orders_pending ?? "—"} testid="tile-open-orders" />
                    <Tile to="/admin/orders" icon={<ClipboardList size={18} strokeWidth={1.5} />} label="Total orders" value={summary?.orders_total ?? "—"} testid="tile-total-orders" />
                    <Tile to="/admin/products" icon={<Package size={18} strokeWidth={1.5} />} label="Products" value={summary?.products ?? "—"} testid="tile-products" />
                    <Tile to="/admin/downloads" icon={<FileDown size={18} strokeWidth={1.5} />} label="Downloads" value={summary?.downloads ?? "—"} testid="tile-downloads" />
                    <Tile to="/admin/promotions" icon={<Megaphone size={18} strokeWidth={1.5} />} label="Active promos" value={summary?.promotions_active ?? "—"} testid="tile-promotions" />
                    <Tile to="/admin/events" icon={<Calendar size={18} strokeWidth={1.5} />} label="Upcoming events" value={summary?.events_upcoming ?? "—"} testid="tile-events" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <Panel title="Recent applications" link="/admin/clinics?status=pending">
                        {recentClinics.length === 0 ? <Empty text="No pending applications." /> : recentClinics.map(c => (
                            <Link key={c.id} to={`/admin/clinics`} className="block p-4 hover:bg-zinc-50">
                                <p className="font-medium text-sm">{c.clinic_name}</p>
                                <p className="text-xs text-zinc-500">{c.contact_name} · {c.email}</p>
                            </Link>
                        ))}
                    </Panel>
                    <Panel title="Recent orders" link="/admin/orders">
                        {recentOrders.length === 0 ? <Empty text="No orders yet." /> : recentOrders.map(o => (
                            <Link key={o.id} to="/admin/orders" className="block p-4 hover:bg-zinc-50">
                                <p className="font-medium text-sm">{o.order_no} · {o.clinic_name}</p>
                                <p className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString()} · {o.items.length} items · ${o.total.toFixed(2)}</p>
                            </Link>
                        ))}
                    </Panel>
                </div>
            </div>
        </Shell>
    );
}

function Tile({ to, icon, label, value, testid, highlight }) {
    return (
        <Link to={to} data-testid={testid} className={`card-base p-5 hover:border-black transition-colors ${highlight ? "border-black" : ""}`}>
            <div className="flex items-center gap-2 mb-3 text-zinc-500">{icon}<span className="eyebrow">{label}</span></div>
            <p className="font-heading text-3xl font-light">{value}</p>
        </Link>
    );
}
function Panel({ title, link, children }) {
    return (
        <div>
            <div className="flex items-baseline justify-between mb-4">
                <p className="eyebrow">{title}</p>
                <Link to={link} className="text-sm text-zinc-500 hover:text-black">View all →</Link>
            </div>
            <div className="card-base divide-y divide-zinc-100">{children}</div>
        </div>
    );
}
function Empty({ text }) { return <div className="p-6 text-sm text-zinc-500">{text}</div>; }
